import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography } from '@mui/material';

const DEFAULT_COLUMNS = [
	{ key: 'teams',        label: 'Team',  source: 'teams'        },
	{ key: 'states',       label: 'State', source: 'states'       },
	{ key: 'positions',    label: 'Pos.',  source: 'positions'    },
	{ key: 'numbers',      label: 'No.',   source: 'numbers'      },
	{ key: 'career_games', label: 'Games', source: 'career_games' },
	{ key: 'age',          label: 'Age',   source: 'age'          },
];

const SOURCE_LEGEND_METADATA = {
	teams:                { group: 'set',      example: 'SYD',  phrase: 'played for this team'         },
	states:               { group: 'set',      example: 'VIC',  phrase: 'is from this state'           },
	numbers:              { group: 'set',      example: '#7',   phrase: 'worn this number'             },
	other_teams:          { group: 'set',      example: 'GWS',  phrase: 'played for another club'      },
	'club_stats.numbers': { group: 'set',      example: '#7',   phrase: 'worn this number at the club' },
	positions:            { group: 'position', example: 'M/F'                                          },
	career_games:         { group: 'numeric',  example: '298',  tolerance: '50 games'                  },
	age:                  { group: 'numeric',  example: '27yr', tolerance: '2 years'                   },
	'club_stats.games':   { group: 'numeric',  example: '50',   tolerance: '50 games'                  },
	'club_stats.debut':   { group: 'numeric',  example: '2015', tolerance: '2 years'                   },
};

const formatList = (items) => {
	if (items.length <= 1) return items[0] ?? '';
	return items.slice(0, -1).join(', ') + ' or ' + items[items.length - 1];
};

const buildLegend = (columns, cl) => {
	const active = columns
		.map(c => ({ col: c, meta: SOURCE_LEGEND_METADATA[c.source] }))
		.filter(({ meta }) => Boolean(meta));

	const setItems     = active.filter(({ meta }) => meta.group === 'set');
	const posItem      = active.find(({ meta })   => meta.group === 'position');
	const numericItems = active.filter(({ meta }) => meta.group === 'numeric');

	const legend = [];

	legend.push({
		example: 'SYD', evaluation: { result: 2, higher: null },
		instruction: `${cl.correct} means this attribute matches the mystery player.`
	});

	if (setItems.length > 0) {
		const labels  = setItems.map(({ col }) => col.label);
		const phrases = setItems.map(({ meta }) => meta.phrase);
		legend.push({
			example: setItems[0].meta.example, evaluation: { result: 1, higher: null },
			instruction: `${cl.partial} for ${formatList(labels)} means the mystery player has ${formatList(phrases)} in the past.`
		});
	}

	if (posItem) {
		legend.push({
			example: posItem.meta.example, evaluation: { result: 1, higher: null },
			instruction: `${cl.partial} for ${posItem.col.label} means the mystery player does play in one of these positions.`
		});
	}

	if (numericItems.length > 0) {
		const labels     = numericItems.map(({ col }) => col.label);
		const tolerances = [...new Set(numericItems.map(({ meta }) => meta.tolerance))];
		legend.push({
			example: numericItems[0].meta.example, evaluation: { result: 1, higher: true },
			instruction: `${cl.partial} for ${formatList(labels)} means the mystery player is within ${formatList(tolerances)} (+/-).`
		});
	}

	legend.push({
		example: '27yr', evaluation: { result: 0, higher: null },
		instruction: `${cl.miss} means this attribute is not a match or a close match.`
	});

	return legend;
};

const DialogClose = (props) => {
	const { children, onClose, ...other } = props;

	return (
		<DialogContent sx={{ m: 0, p: 2 }} {...other}>
			{onClose ? (
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500]
					}}
				>
					<CloseIcon />
				</IconButton>
			) : null}
		</DialogContent>
	);
};

const ClueArrow = (props) => {
  const style = { width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent' };

  if (props.higher) {
    style.borderBottom = '5px solid black';
  } else {
    style.borderTop = '5px solid black';
  }

  return <Box sx={style} />;
};

const LegendItem = (props) => {
  let color = 'default';
  if (props.evaluation.result === 1) {
    color = 'warning';
  } else if (props.evaluation.result === 2) {
    color = 'success';
  }

  let label = (<Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ padding: 0, pt: 0.5, pb: 0.5 }}>
    <Typography variant="caption">{props.example}</Typography>
    {props.evaluation.higher !== null && <ClueArrow higher={props.evaluation.higher} />}
  </Stack>);

  return (
    <Stack direction="row" spacing={2}>
      <Chip sx={{ width: '80px', display: "flex", borderRadius: 0, height: '100%', '.MuiChip-label': { padding: 0 }, padding: 0 }} label={label} color={color} />
      <Typography variant="body2" sx={{ width: "100%" }}>{props.instruction}</Typography>
    </Stack>
  );
}

const Help = (props) => {
  const isLoading = props.isLoading;
  const maxGuesses = props.gameType?.max_guesses || props.MAX_GUESSES;
  const description = props.gameType?.description || null;
  const cl = props.colorLabels || { correct: 'Green', partial: 'Amber', miss: 'Grey' };
  const columns = props.columnConfig?.columns || props.gameType?.column_config?.columns || DEFAULT_COLUMNS;
  const legend = buildLegend(columns, cl);

	return (
		<Dialog open={props.showHelp} onClose={() => props.setShowHelp(false)}>
			<DialogClose id="dialog-title-help" onClose={() => props.setShowHelp(false)} />
      <DialogTitle textAlign="center">How to Play</DialogTitle>
      {props.gameType?.name && (
        <Typography variant="subtitle2" textAlign="center" sx={{ mt: -1, mb: 1, color: 'text.secondary' }}>
          {props.gameType.name}
        </Typography>
      )}
			<DialogContent>
				{isLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          : <Stack direction="column" spacing={2}>
              {description
                ? <Typography variant="body2"><b>You have {maxGuesses} guesses to identify the mystery AFL player.</b> {description}</Typography>
                : <Typography variant="body2"><b>You have {maxGuesses} guesses to identify the mystery AFL player.</b> Attributes are as at the end of the last completed round of the regular season and only current players who have played at least one regular season game in their career are possible answers.</Typography>
              }
              {legend.map((item, i) => (
                <LegendItem key={i} example={item.example} evaluation={item.evaluation} instruction={item.instruction} />
              ))}
              <Typography variant="body2">There is a new mystery player every day. Good luck!</Typography>
            </Stack>
        }
			</DialogContent>
		</Dialog>
	);
};

export default Help;
